import React from 'react';
import { Box, Typography } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { EDIT_OBJECT_TOKENS } from './editObjectTokens';

export interface ReviewHeroProps {
  /** Cover image URL — when omitted, a placeholder icon renders. */
  coverImageUrl?: string | null;
  /** Cross-origin attribute for the cover image. */
  coverCrossOrigin?: '' | 'anonymous' | 'use-credentials';
  /** Hero title (e.g. artwork title). Falls back to "Untitled". */
  title?: string;
  /** Italic subtitle line (e.g. "Jane Doe · 2024"). */
  subtitle?: string;
  /** Comma-or-bullet-separated meta facts (e.g. work type, dimensions). */
  metaLine?: React.ReactNode;
  /** Secondary meta facts rendered on a new row (e.g. signature notes). */
  metaLineSecondary?: React.ReactNode;
  /** Catalog ID rendered as monospace below the meta line. */
  catalogId?: string;
  /** Optional right-column slot (visibility chip, status badge). */
  rightSlot?: React.ReactNode;
}

/**
 * Wide hero banner shown at the top of the Review step. Mirrors the
 * design's `rs-hero` block: 160px square cover thumbnail on the left,
 * serif title + italic subtitle + meta facts in the center, and an
 * optional right-aligned slot for visibility chips. Used inside
 * ReviewTab so both the legacy and redesigned wizards get the upgrade.
 */
export const ReviewHero: React.FC<ReviewHeroProps> = ({
  coverImageUrl,
  coverCrossOrigin,
  title,
  subtitle,
  metaLine,
  metaLineSecondary,
  catalogId,
  rightSlot,
}) => {
  const displayTitle = title?.trim() || 'Untitled';

  return (
    <Box
      data-testid="review-hero"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '96px 1fr', md: '96px 1fr auto' },
        gap: { xs: 2, md: 3 },
        alignItems: 'center',
        p: { xs: 2, md: 2.5 },
        mb: 2,
        borderRadius: `${EDIT_OBJECT_TOKENS.radius.md}px`,
        border: `1px solid ${EDIT_OBJECT_TOKENS.color.lineSoft}`,
        background: `linear-gradient(180deg, ${EDIT_OBJECT_TOKENS.color.aliRed050} 0%, #fff 100%)`,
      }}
    >
      <Box
        data-testid="review-hero-image"
        sx={{
          width: { xs: 96, md: 96 },
          height: { xs: 96, md: 96 },
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: EDIT_OBJECT_TOKENS.color.canvasDim,
          border: `1px solid ${EDIT_OBJECT_TOKENS.color.lineSoft}`,
          display: 'grid',
          placeItems: 'center',
          color: EDIT_OBJECT_TOKENS.color.slate300,
        }}
      >
        {coverImageUrl ? (
          <Box
            component="img"
            src={coverImageUrl}
            crossOrigin={coverCrossOrigin}
            alt={displayTitle}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <ImageIcon sx={{ fontSize: 48 }} />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h2"
          data-testid="review-hero-title"
          sx={{
            fontFamily: EDIT_OBJECT_TOKENS.font.serif,
            fontWeight: 700,
            fontSize: { xs: '1.4rem', md: '1.75rem' },
            lineHeight: 1.2,
            color: EDIT_OBJECT_TOKENS.color.aliRed700,
            m: 0,
            mb: 0.5,
            wordBreak: 'break-word',
          }}
        >
          {displayTitle}
        </Typography>
        {subtitle && (
          <Typography
            data-testid="review-hero-subtitle"
            sx={{
              fontFamily: EDIT_OBJECT_TOKENS.font.serif,
              fontStyle: 'italic',
              fontSize: '1rem',
              lineHeight: 1.4,
              color: EDIT_OBJECT_TOKENS.color.slate700,
              mb: metaLine || catalogId ? 1 : 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
        {metaLine && (
          <Typography
            data-testid="review-hero-meta"
            sx={{
              fontSize: 13,
              color: EDIT_OBJECT_TOKENS.color.slate500,
              lineHeight: 1.6,
              '& strong': { color: EDIT_OBJECT_TOKENS.color.ink900, fontWeight: 600 },
            }}
          >
            {metaLine}
          </Typography>
        )}
        {metaLineSecondary && (
          <Typography
            data-testid="review-hero-meta-secondary"
            sx={{
              fontSize: 13,
              color: EDIT_OBJECT_TOKENS.color.slate500,
              lineHeight: 1.6,
            }}
          >
            {metaLineSecondary}
          </Typography>
        )}
        {catalogId && (
          <Typography
            data-testid="review-hero-catalog-id"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              color: EDIT_OBJECT_TOKENS.color.slate500,
              mt: 0.5,
            }}
          >
            Catalog: {catalogId}
          </Typography>
        )}
      </Box>

      {rightSlot && (
        <Box
          data-testid="review-hero-right-slot"
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          {rightSlot}
        </Box>
      )}
    </Box>
  );
};
